export interface DatedPeriodItem {
  date: string
}

export interface PeriodInformationDay extends DatedPeriodItem {
  id: string
  note?: string | null
  dayMeals?: readonly unknown[]
}

export interface PeriodSelectionReference {
  weekDayId: string
  confirmed?: boolean
}

export interface PeriodRangeAnalysis {
  removedDayCount: number
  affectedDayCount: number
  menuOptionCount: number
  selectionCount: number
  confirmedSelectionCount: number
  removedDates: string[]
}

function parseDateOnly(value: string): Date {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) throw new Error(`Fecha inválida: ${value}`)
  const date = new Date(`${value}T00:00:00.000Z`)
  if (Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value) {
    throw new Error(`Fecha inválida: ${value}`)
  }
  return date
}

export function enumeratePeriodDates(startDate: string, endDate: string): string[] {
  const start = parseDateOnly(startDate)
  const end = parseDateOnly(endDate)
  if (end < start) throw new Error('La fecha final debe ser igual o posterior a la fecha inicial.')

  const result: string[] = []
  for (let cursor = start.getTime(); cursor <= end.getTime(); cursor += 86_400_000) {
    result.push(new Date(cursor).toISOString().slice(0, 10))
  }
  return result
}

export function itemsOutsidePeriod<T extends DatedPeriodItem>(
  items: readonly T[],
  startDate: string,
  endDate: string,
): T[] {
  return items.filter((item) => item.date < startDate || item.date > endDate)
}

export function reconcilePeriodItems<T extends DatedPeriodItem>(
  existingItems: readonly T[],
  startDate: string,
  endDate: string,
  createItem: (date: string) => T,
): { items: T[]; removedItems: T[] } {
  const existingByDate = new Map(existingItems.map((item) => [item.date, item]))
  const dates = enumeratePeriodDates(startDate, endDate)
  return {
    items: dates.map((date) => existingByDate.get(date) ?? createItem(date)),
    removedItems: itemsOutsidePeriod(existingItems, startDate, endDate),
  }
}

export function analyzePeriodRange(
  days: readonly PeriodInformationDay[],
  selections: readonly PeriodSelectionReference[],
  startDate: string,
  endDate: string,
): PeriodRangeAnalysis {
  enumeratePeriodDates(startDate, endDate)
  const removedDays = itemsOutsidePeriod(days, startDate, endDate)
  const removedDayIds = new Set(removedDays.map((day) => day.id))
  const removedSelections = selections.filter((selection) => removedDayIds.has(selection.weekDayId))
  const selectionDays = new Set(removedSelections.map((selection) => selection.weekDayId))
  return {
    removedDayCount: removedDays.length,
    affectedDayCount: removedDays.filter(
      (day) => Boolean(day.note) || (day.dayMeals?.length ?? 0) > 0 || selectionDays.has(day.id),
    ).length,
    menuOptionCount: removedDays.reduce((total, day) => total + (day.dayMeals?.length ?? 0), 0),
    selectionCount: removedSelections.length,
    confirmedSelectionCount: removedSelections.filter((selection) => selection.confirmed).length,
    removedDates: removedDays.map((day) => day.date).sort(),
  }
}
